export const Z_SCORES: Record<number, number> = {
  0.80: 1.282,
  0.90: 1.645,
  0.95: 1.96,
};

export const MODELS = [
  { id: "chatgpt", label: "ChatGPT" },
  { id: "aioverview", label: "Google AI Overviews" },
  { id: "gemini", label: "Gemini" },
  { id: "claude", label: "Claude" },
  { id: "perplexity", label: "Perplexity" },
  { id: "copilot", label: "Copilot" },
  { id: "deepseek", label: "DeepSeek" },
] as const;

export type ModelId = (typeof MODELS)[number]["id"];

export const DEFAULT_MODELS: ModelId[] = ["chatgpt", "aioverview", "gemini"];

export const FREQUENCIES = [
  { key: "daily", runs: 30, label: "Daily", freq: "30 runs/prompt/mo" },
  {
    key: "three",
    runs: 10,
    label: "Every 3 Days",
    freq: "10 runs/prompt/mo",
  },
  { key: "weekly", runs: 5, label: "Weekly", freq: "5 runs/prompt/mo" },
] as const;

export type FrequencyKey = (typeof FREQUENCIES)[number]["key"];

export const MINS_PER_RUN = 2.5;

export function getZ(conf: number): number {
  return Z_SCORES[conf] ?? 1.645;
}

/** Margin of error (half-width of CI) as a proportion 0–1, capped at 1 */
export function ciHalfWidth(n: number, p: number, conf: number): number {
  if (n < 1) return 1;
  return Math.min(1, getZ(conf) * Math.sqrt((p * (1 - p)) / n));
}

/** Margin of error as a percentage 0–100 */
export function marginError(n: number, p: number, conf: number): number {
  return Math.min(100, ciHalfWidth(n, p, conf) * 100);
}

/** Minimum detectable change (two-sided, same n each group), proportion 0–1 */
export function mdc(n: number, p: number, conf: number): number {
  if (n < 1) return 1;
  return Math.min(1, getZ(conf) * Math.sqrt((2 * p * (1 - p)) / n));
}

/** MDC as a percentage 0–100 */
export function mdcPct(n: number, p: number, conf: number): number {
  return Math.min(100, mdc(n, p, conf) * 100);
}

/** Quality rating based on margin of error */
export function qualityRating(me: number): {
  label: string;
  variant: "success" | "warning" | "error";
} {
  if (me < 8) return { label: "Reliable", variant: "success" };
  if (me < 15) return { label: "Directional", variant: "warning" };
  return { label: "Exploratory", variant: "error" };
}

/** Generate CI chart curve data */
export function generateCurveData(
  p: number,
  conf: number,
): Array<{ observations: number; value: number }> {
  const xMin = 5;
  const xMax = 600;
  const points: Array<{ observations: number; value: number }> = [];
  for (let i = 0; i < 200; i++) {
    const x = xMin + (i / 199) * (xMax - xMin);
    points.push({ observations: Math.round(x), value: ciHalfWidth(x, p, conf) * 100 });
  }
  return points;
}

/** Generate MDC chart curve data */
export function generateMdcCurveData(
  p: number,
  conf: number,
): Array<{ observations: number; value: number }> {
  const xMin = 5;
  const xMax = 600;
  const points: Array<{ observations: number; value: number }> = [];
  for (let i = 0; i < 200; i++) {
    const x = xMin + (i / 199) * (xMax - xMin);
    points.push({ observations: Math.round(x), value: mdc(x, p, conf) * 100 });
  }
  return points;
}

/** Generate heatmap data */
export function generateHeatmapData(
  p: number,
  conf: number,
): Array<{
  frequency: string;
  runs: number;
  cells: Array<{ prompts: number; me: number; quality: string }>;
}> {
  const promptCounts = [5, 10, 15, 20, 25, 30, 40, 50];
  return FREQUENCIES.map((f) => ({
    frequency: f.label,
    runs: f.runs,
    cells: promptCounts.map((pc) => {
      const total = pc * f.runs;
      const me = marginError(total, p, conf);
      let quality = "poor";
      if (me < 8) quality = "good";
      else if (me < 12) quality = "ok";
      else if (me < 20) quality = "weak";
      return { prompts: pc, me, quality };
    }),
  }));
}

/** Calculate effort data for manual tracking */
export function calculateEffort(
  prompts: number,
  numModels: number,
  p: number,
  conf: number,
) {
  return FREQUENCIES.map((f) => {
    const totalRunsPerModel = prompts * f.runs;
    const totalRuns = totalRunsPerModel * numModels;
    const totalMins = totalRuns * MINS_PER_RUN;
    const hours = totalMins / 60;
    const hoursPerWeek = hours / 4.3;
    const me = marginError(totalRunsPerModel, p, conf);

    return {
      key: f.key,
      label: f.label,
      totalRunsPerModel,
      totalRuns,
      hours: Number(hours.toFixed(1)),
      hoursPerWeek: Number(hoursPerWeek.toFixed(1)),
      me: Number(me.toFixed(1)),
    };
  });
}

/** Calculate how much effort is needed to reach ±10% accuracy manually */
export function calculateManualBenchmark(
  prompts: number,
  numModels: number,
  p: number,
  conf: number,
) {
  const z = getZ(conf);
  const nNeeded = Math.ceil(Math.pow(z / 0.1, 2) * p * (1 - p));
  const totalManualRuns = nNeeded * numModels * prompts;
  const totalManualMins = totalManualRuns * MINS_PER_RUN;
  const totalManualHours = Number((totalManualMins / 60).toFixed(1));
  const totalManualHoursPerWeek = Number(
    (totalManualMins / 60 / 4.3).toFixed(1),
  );

  return {
    nNeeded,
    totalManualRuns,
    totalManualHours,
    totalManualHoursPerWeek,
  };
}
